import { extractUserFromResponse } from "@/feature/auth/lib/auth-session";
import { setUser } from "@/feature/auth/state/auth-slice";
import { baseApi } from "@/redux/api/base-api";

type AuthApiResponse = Record<string, unknown>;

function toApiGender(gender: "male" | "female" | "other") {
  if (gender === "male") return "Male";
  if (gender === "female") return "Female";
  return "Other";
}

export type LoginRequest = {
  user_or_email: string;
  password: string;
};

export type Login2FaRequest = {
  user_or_email: string;
  otp: string;
};

export type RegisterRequest = {
  email: string;
  name: string;
  password: string;
  gender: "male" | "female" | "other";
  phone_number: string;
};

export type RegisterCompleteRequest = {
  token: string;
};

export type SendEmailOtpRequest = {
  type:
  | "reset_admin"
  | "reset_agent"
  | "reset_b2c"
  | "register_agent"
  | "register_b2c";
  email: string;
};

export type MatchEmailOtpRequest = {
  type:
  | "reset_admin"
  | "reset_agent"
  | "reset_b2c"
  | "register_agent"
  | "register_b2c";
  email: string;
  otp: string;
};

export type ResetPasswordRequest = {
  token: string;
  password: string;
};

export type ChangeProfilePasswordRequest = {
  old_password: string;
  new_password: string;
};

function syncUserFromAuthResponse(
  dispatch: (action: unknown) => unknown,
  payload: unknown,
) {
  const user = extractUserFromResponse(payload);
  if (!user) {
    return;
  }

  dispatch(setUser(user));
}

export const authApi = baseApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    login: builder.mutation<AuthApiResponse, LoginRequest>({
      query: (body) => ({
        url: "/auth/b2c/login",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AuthProfile"],
    }),
    login2fa: builder.mutation<AuthApiResponse, Login2FaRequest>({
      query: (body) => ({
        url: "/auth/b2c/login/2fa",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AuthProfile"],
    }),
    register: builder.mutation<AuthApiResponse, RegisterRequest>({
      query: (body) => ({
        url: "/auth/b2c/register",
        method: "POST",
        body: {
          ...body,
          gender: toApiGender(body.gender),
        },
      }),
    }),
    registerComplete: builder.mutation<AuthApiResponse, RegisterCompleteRequest>({
      query: (body) => ({
        url: "/auth/b2c/register/complete",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AuthProfile"],
    }),
    sendEmailOtp: builder.mutation<AuthApiResponse, SendEmailOtpRequest>({
      query: (body) => ({
        url: "/public/email-otp/send",
        method: "POST",
        body,
      }),
    }),
    matchEmailOtp: builder.mutation<AuthApiResponse, MatchEmailOtpRequest>({
      query: (body) => ({
        url: "/public/email-otp/match",
        method: "POST",
        body,
      }),
    }),
    resetPassword: builder.mutation<AuthApiResponse, ResetPasswordRequest>({
      query: (body) => ({
        url: "/auth/b2c/reset-password",
        method: "POST",
        body,
      }),
    }),
    getProfile: builder.query<AuthApiResponse, void>({
      query: () => ({
        url: "/b2c/profile",
        method: "GET",
      }),
      providesTags: ["AuthProfile"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          syncUserFromAuthResponse(dispatch, data);
        } catch {
          // Keep auth state unchanged if profile fetch fails.
        }
      },
    }),
    updateProfile: builder.mutation<AuthApiResponse, FormData>({
      query: (body) => {
        return {
          url: "/b2c/profile",
          method: "PATCH",
          body,
        };
      },
      invalidatesTags: ["AuthProfile"],
    }),
    changeProfilePassword: builder.mutation<
      AuthApiResponse,
      ChangeProfilePasswordRequest
    >({
      query: (body) => ({
        url: "/b2c/profile/change-password",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AuthProfile"],
    }),
  }),
});

export const {
  useLoginMutation,
  useLogin2faMutation,
  useRegisterMutation,
  useRegisterCompleteMutation,
  useSendEmailOtpMutation,
  useMatchEmailOtpMutation,
  useResetPasswordMutation,
  useGetProfileQuery,
  useUpdateProfileMutation,
  useChangeProfilePasswordMutation,
} = authApi;
